import type {
  CalendarId,
  ISODateTime,
  Minutes,
  Money,
  Percent,
  TaskId,
} from './branded.js';

/**
 * 工作分解結構的任務型別（決定工期／工時／單位三者的鎖定關係）。
 * - FixedUnits：單位固定，改工時 → 改工期。
 * - FixedDuration：工期固定，改工時 → 改單位。
 * - FixedWork：工時固定（隱含 effortDriven = true），改單位 → 改工期。
 */
export type TaskType = 'FixedUnits' | 'FixedDuration' | 'FixedWork';

/** MS Project 的 8 種限制條件。 */
export type ConstraintType =
  /** As Soon As Possible（順排預設） */
  | 'ASAP'
  /** As Late As Possible（逆排預設） */
  | 'ALAP'
  /** Start No Earlier Than */
  | 'SNET'
  /** Start No Later Than */
  | 'SNLT'
  /** Finish No Earlier Than */
  | 'FNET'
  /** Finish No Later Than */
  | 'FNLT'
  /** Must Start On（硬性） */
  | 'MSO'
  /** Must Finish On（硬性） */
  | 'MFO';

/** 成本累計方式。 */
export type AccrualType = 'Start' | 'Prorated' | 'End';

/**
 * 進度來源。
 * - `work`：由指派的實際／剩餘工時推導 percentComplete（預設）。
 * - `manual`：percentComplete 由人直接指定，回報的工時只作成本檢視用。
 */
export type TaskProgressMode = 'work' | 'manual';

/** 看板狀態欄位。 */
export type TaskWorkflowStatus = 'todo' | 'inProgress' | 'review' | 'done';

/** 任務優先權，0–1000；1000 = 撫平時絕不延後（Do Not Level）。 */
export type TaskPriority = number;

export interface Task {
  readonly id: TaskId;
  /** 穩定的全域唯一編號（匯入／匯出 MSPDI 時對應 <UID>）。 */
  readonly uid: number;
  /** WBS 碼，例 "1.2.3"；由大綱結構推導。 */
  readonly wbs: string;
  readonly name: string;

  // --- 大綱結構 ---
  /** 大綱層級，1 為最上層。 */
  readonly outlineLevel: number;
  readonly parentId: TaskId | null;

  // --- 工期與工時 ---
  readonly durationMinutes: Minutes;
  /** 彙總的指派總工時。 */
  readonly work: Minutes;

  // --- 排程結果（由排程引擎寫入） ---
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
  readonly earlyStart: ISODateTime;
  readonly earlyFinish: ISODateTime;
  readonly lateStart: ISODateTime;
  readonly lateFinish: ISODateTime;
  /** 總浮時；<= 0 代表在要徑上。 */
  readonly totalFloat: Minutes;
  /** 自由浮時：不影響任一後續任務最早開始的可延遲量。 */
  readonly freeFloat: Minutes;
  readonly isCritical: boolean;

  // --- 分類旗標 ---
  readonly isSummary: boolean;
  readonly isMilestone: boolean;
  /** 手動排程：引擎不得改動其 start/finish。 */
  readonly isManuallyScheduled: boolean;

  // --- 排程行為 ---
  readonly taskType: TaskType;
  /** 工作導向：增減資源會改變工期而非工時。 */
  readonly effortDriven: boolean;
  readonly constraintType: ConstraintType;
  /** ASAP / ALAP 時為 null。 */
  readonly constraintDate: ISODateTime | null;
  /** 期限；超過只產生警示，不影響排程。 */
  readonly deadline: ISODateTime | null;

  // --- 進度 ---
  readonly percentComplete: Percent;
  readonly percentWorkComplete: Percent;
  readonly actualStart: ISODateTime | null;
  readonly actualFinish: ISODateTime | null;
  /** 進度來源；省略等同 `'work'`（舊檔相容）。 */
  readonly progressMode?: TaskProgressMode;
  /** 看板狀態；省略時由 `effectiveWorkflowStatus()` 依進度推導。 */
  readonly workflowStatus?: TaskWorkflowStatus;

  // --- 其他 ---
  readonly priority: TaskPriority;
  /** 任務日曆；null 代表沿用專案日曆。 */
  readonly calendarId: CalendarId | null;
  /** 是否排程時忽略已指派 Work 資源的日曆；預設 false（採共同工作時間）。 */
  readonly ignoreResourceCalendar: boolean;
  readonly fixedCost: Money;
  readonly fixedCostAccrual: AccrualType;
  readonly notes: string;

  /** 自訂欄位值，key 為 CustomFieldDefinition.id。 */
  readonly customFieldValues?: Readonly<Record<string, CustomFieldValue>>;
}

/** 自訂欄位可承載的純值型別（避免使用 any）。 */
export type CustomFieldValue = string | number | boolean | null;

/** 任務的進度來源；省略時預設 `'work'`。 */
export function taskProgressMode(task: Task): TaskProgressMode {
  return task.progressMode ?? 'work';
}

/**
 * 看板狀態的唯一判讀入口。顯式值優先，但兩條規則永遠凌駕其上，
 * 避免看板欄位與進度百分比互相矛盾：
 * - `percentComplete` 為 100 一律視為 `done`。
 * - 顯式 `todo` 但已有進度時視為 `inProgress`。
 * 沒有顯式值時純由進度推導：0 → todo、0 < p < 100 → inProgress、100 → done。
 */
export function effectiveWorkflowStatus(task: Task): TaskWorkflowStatus {
  const pct = task.percentComplete as number;
  if (pct >= 100) return 'done';
  const explicit = task.workflowStatus;
  if (explicit === undefined) return pct > 0 ? 'inProgress' : 'todo';
  if (explicit === 'done') return pct > 0 ? 'inProgress' : 'todo';
  if (explicit === 'todo' && pct > 0) return 'inProgress';
  return explicit;
}
